import { useEffect, useState } from 'react';

import type { TSectionElement } from '@shared/dream-components';

import type { NodeContent, Page } from '~/interfaces/page';
import type { Publication } from '~/interfaces/publication';
import { getColors } from '~/utils/findAndInsertProprietaryNode/getColors';

const checkOverflow = () => document.body.scrollHeight > window.innerHeight;

const useProprietaryFooter = (
  pageData: Page['viewable_page_version']['content'],
  webTheme: Publication['web_theme'],
) => {
  const [isPageContentOverflowing, setIsPageContentOverflowing] = useState(false);

  useEffect(() => {
    const syncOverflowState = () => setIsPageContentOverflowing(checkOverflow());

    syncOverflowState();

    const observer = new ResizeObserver(syncOverflowState);
    observer.observe(document.body);

    window.addEventListener('resize', syncOverflowState);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncOverflowState);
    };
  }, []);

  if (!pageData || !pageData.content) {
    return {
      lastSection: undefined,
      isPageContentOverflowing,
      pageHasOneSection: false,
    };
  }

  const docContent = pageData.content as NodeContent[];
  const pageHasOneSection =
    docContent.length === 1 &&
    docContent[0].type === 'section' &&
    (docContent[0] as TSectionElement).attrs.isViewportHeight === true;

  const lastSection = (pageHasOneSection ? docContent[0] : docContent[docContent.length - 1]) as TSectionElement;

  const { textColor } = getColors(lastSection, webTheme);

  return {
    isPageContentOverflowing,
    pageHasOneSection,
    backgroundColor: lastSection?.attrs?.backgroundColor,
    textColor,
  };
};

export default useProprietaryFooter;
